import { useEffect, useMemo, useState } from 'react';

function parseLocalDateTime(value) {
  if (!value) return null;
  if (value instanceof Date) return value;

  const m = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (m) {
    const [, y, mo, d, h, mi, s] = m;
    return new Date(+y, +mo - 1, +d, +h, +mi, s ? +s : 0, 0);
  }

  const dt = new Date(value);
  return isNaN(dt) ? null : dt;
}

function formatDateTime(value) {
  const dt = parseLocalDateTime(value);
  if (!dt || isNaN(dt)) return '–';
  return dt.toLocaleString('no-NO');
}

function getRentalItems(order) {
  return (order?.OrderItems || []).filter((item) => item.date_from && item.date_to);
}

function getEarliestRentalStart(order) {
  const rentals = getRentalItems(order)
    .map((item) => parseLocalDateTime(item.date_from))
    .filter(Boolean)
    .sort((a, b) => a - b);

  return rentals[0] || null;
}

function getLatestRentalEnd(order) {
  const rentals = getRentalItems(order)
    .map((item) => parseLocalDateTime(item.date_to))
    .filter(Boolean)
    .sort((a, b) => b - a);

  return rentals[0] || null;
}

function getRentalWindow(order) {
  return {
    start: getEarliestRentalStart(order),
    end: getLatestRentalEnd(order),
  };
}

function getProductSummary(order) {
  const items = order?.OrderItems || [];
  if (!items.length) return '–';

  const names = [...new Set(items.map((i) => i.name).filter(Boolean))];
  if (names.length === 0) return '–';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]}, ${names[1]}`;
  return `${names[0]}, ${names[1]} + ${names.length - 2} til`;
}

function normalizeText(s) {
  return String(s || '').trim().toLowerCase();
}

function isCancelledStatus(name) {
  const n = normalizeText(name);
  return n.includes('kansell');
}

function isCompletedPaidStatus(name) {
  const n = normalizeText(name);
  return (
    n.includes('betalt') ||
    n.includes('ferdig') ||
    n.includes('gjennomført') ||
    n.includes('fullført')
  );
}

function getOrderStatusName(order, statusOptions) {
  return (
    statusOptions.find((s) => s.id === order.StatusId)?.name ||
    order.Status?.name ||
    order.status ||
    ''
  );
}

function getOrderColorClasses(order, statusOptions) {
  const statusName = getOrderStatusName(order, statusOptions);
  const rentalEnd = getLatestRentalEnd(order);
  const now = new Date();

  if (isCancelledStatus(statusName)) {
    return {
      outer: 'border-red-200 bg-red-50 hover:bg-red-100',
      badge: 'bg-red-100 text-red-800',
    };
  }

  // Grønn kun hvis betalt/ferdig OG leieperioden faktisk er over
  if (isCompletedPaidStatus(statusName) && rentalEnd && rentalEnd < now) {
    return {
      outer: 'border-green-200 bg-green-50 hover:bg-green-100',
      badge: 'bg-green-100 text-green-800',
    };
  }

  // Alt annet som ikke er kansellert = gul
  return {
    outer: 'border-yellow-200 bg-yellow-50 hover:bg-yellow-100',
    badge: 'bg-yellow-100 text-yellow-800',
  };
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [mode, setMode] = useState('view'); // view | edit | new
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');

  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  // --- Charges (etterfakturering) ---
  const [charges, setCharges] = useState([]);
  const [chargesError, setChargesError] = useState('');
  const [chargesSuccess, setChargesSuccess] = useState('');
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [chargeRows, setChargeRows] = useState([
    { description: '', amount: '', paymentId: '' }
  ]);

  const API = process.env.REACT_APP_API_URL;

  // --- INIT ---
  useEffect(() => { fetchOrders(); }, []);
  useEffect(() => {
    if (mode === 'new' || mode === 'edit') {
      fetchUsers();
      fetchProducts();
      fetchPayments();
      fetchStatuses();
    }
  }, [mode]);

  useEffect(() => {
    if (showChargeForm && paymentOptions.length === 0) {
      fetchPayments();
    }
  }, [showChargeForm]); // eslint-disable-line

  useEffect(() => {
    if (selected?.id) {
      fetchCharges(selected.id);
      setShowChargeForm(false);
      setChargeRows([{ description: '', amount: '', paymentId: '' }]);
      setChargesError('');
      setChargesSuccess('');
    } else {
      setCharges([]);
    }
  }, [selected?.id]); // eslint-disable-line

  // --- API ---
  async function fetchOrders() {
    try {
      const res = await fetch(`${API}/orders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error();
      setOrders(await res.json());
    } catch {
      setError('Kunne ikke hente ordrer');
    }
  }

  async function fetchUsers() {
    try {
      const res = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error();
      setUsers(await res.json());
    } catch {}
  }

  async function fetchProducts() {
    try {
      const res = await fetch(`${API}/products`);
      if (!res.ok) throw new Error();
      setProducts(await res.json());
    } catch {}
  }

  async function fetchPayments() {
    try {
      const res = await fetch(`${API}/payments`);
      if (!res.ok) throw new Error();
      setPaymentOptions(await res.json());
    } catch {}
  }

  async function fetchStatuses() {
    try {
      const res = await fetch(`${API}/statuses`);
      if (!res.ok) throw new Error();
      setStatusOptions(await res.json());
    } catch {}
  }

  // --- CHARGES API ---
  async function fetchCharges(orderId) {
    try {
      const res = await fetch(`${API}/orders/${orderId}/charges`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error();
      setCharges(await res.json());
    } catch {
      setCharges([]);
      setChargesError('Kunne ikke hente etterfakturering for denne ordren');
    }
  }

  async function saveCharges() {
    if (!selected?.id) return;
    setChargesError('');
    setChargesSuccess('');

    const rows = chargeRows
      .map(r => ({
        description: (r.description || '').trim(),
        amount: typeof r.amount === 'string' ? r.amount.replace(',', '.') : r.amount,
        paymentId: r.paymentId === '' ? null : Number(r.paymentId)
      }))
      .filter(r => r.description && parseFloat(r.amount) > 0);

    if (rows.length === 0) {
      setChargesError('Fyll inn minst én rad med beskrivelse og beløp.');
      return;
    }

    try {
      for (const row of rows) {
        const res = await fetch(`${API}/orders/${selected.id}/charges`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            description: row.description,
            amount: row.amount,
            paymentId: row.paymentId,
            isSettled: false
          })
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || 'Feil ved lagring av gebyr');
      }

      await fetchCharges(selected.id);
      setChargesSuccess('Gebyrene er lagret.');
      setShowChargeForm(false);
      setChargeRows([{ description: '', amount: '', paymentId: '' }]);
    } catch (e) {
      setChargesError(e.message);
    }
  }

  async function toggleChargeSettled(chargeId, current) {
    try {
      const res = await fetch(`${API}/charges/${chargeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isSettled: !current })
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Kunne ikke oppdatere status');
      if (selected?.id) await fetchCharges(selected.id);
    } catch (e) {
      setChargesError(e.message);
    }
  }

  async function deleteCharge(chargeId) {
    if (!window.confirm('Slette gebyr?')) return;
    try {
      const res = await fetch(`${API}/charges/${chargeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Kunne ikke slette gebyr');
      if (selected?.id) await fetchCharges(selected.id);
    } catch (e) {
      setChargesError(e.message);
    }
  }

  // --- SØK + SORTERING ---
  const filteredOrders = useMemo(() => {
    const filtered = orders.filter(order => {
      if (!search) return true;
      const s = search.toLowerCase();

      const productText = (order.OrderItems || [])
        .map(i => i.name || '')
        .join(' ')
        .toLowerCase();

      return (
        order.id.toString().includes(s) ||
        (order.User?.name || '').toLowerCase().includes(s) ||
        (order.User?.email || '').toLowerCase().includes(s) ||
        (order.User?.orgNumber || '').toLowerCase().includes(s) ||
        productText.includes(s)
      );
    });

    return filtered.sort((a, b) => {
      const aStart = getEarliestRentalStart(a);
      const bStart = getEarliestRentalStart(b);

      if (aStart && bStart) return bStart - aStart;
      if (aStart && !bStart) return -1;
      if (!aStart && bStart) return 1;

      const aCreated = parseLocalDateTime(a.createdAt);
      const bCreated = parseLocalDateTime(b.createdAt);
      return (aCreated?.getTime() || 0) - (bCreated?.getTime() || 0);
    });
  }, [orders, search]);

  // --- ORDRELINJER (SKJEMA) ---
  function addOrderLine() {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { productId: '', date_from: '', date_to: '', price: '' }]
    }));
  }

  function removeOrderLine(idx) {
    setForm(prev => ({
      ...prev,
      items: prev.items.length === 1 ? prev.items : prev.items.filter((_, i) => i !== idx)
    }));
  }

  function handleOrderLineChange(idx, field, value) {
    setForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === idx
          ? field === 'productId'
            ? {
                ...item,
                productId: value,
                name: products.find(p => String(p.id) === String(value))?.name || '',
                price: value
                  ? products.find(p => p.id.toString() === value.toString())?.price_day || ''
                  : ''
              }
            : {
              ...item,
              [field]: field === 'quantity' ? Number(value) : value
            }
          : item
      )
    }));
  }

  // --- FORM ENDRE ---
  function handleEdit() {
    setForm({
      id: selected.id,
      userId: selected.UserId || selected.User?.id || '',
      status: selected.status,
      statusId: selected.StatusId || selected.Status?.id || '',
      items: (selected.OrderItems || []).map(item => ({
        id: item.id,
        productId: item.productId,
        name: item.name,
        date_from: item.date_from,
        date_to: item.date_to,
        price: item.price,
        quantity: item.quantity || 1
      })),
      paymentId: selected.paymentId || selected.PaymentOption?.id || ''
    });
    setMode('edit');
    setError('');
    setSuccess('');
  }

  // --- FORM NY ---
  function handleNew() {
    setForm({
      userId: '',
      status: 'booked',
      statusId: '',
      items: [{ productId: '', name: '', date_from: '', date_to: '', price: '' }],
      paymentId: ''
    });
    setSelected(null);
    setMode('new');
    setError('');
    setSuccess('');
  }

  // --- FORM ENDRE FELT ---
  function handleFormChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));
  }

// --- LAGRE/OPPDATER ORDRE ---
async function handleFormSubmit(e) {
  e.preventDefault();
  setError('');
  setSuccess('');

  const isEdit = mode === 'edit';

  const itemsUnchanged =
    isEdit &&
    selected?.OrderItems?.length === form.items.length &&
    selected.OrderItems.every((oldItem, idx) => {
      const newItem = form.items[idx];

      return (
        String(oldItem.productId) === String(newItem.productId) &&
        String(oldItem.date_from || '') === String(newItem.date_from || '') &&
        String(oldItem.date_to || '') === String(newItem.date_to || '') &&
        String(oldItem.price || '') === String(newItem.price || '') &&
        String(oldItem.quantity || 1) === String(newItem.quantity || 1)
      );
    });

  let payload;

  if (isEdit && itemsUnchanged) {
    // Kun status/bruker/betaling endres.
    // Ikke send items, så backend ikke rører kalender/availability.
    payload = {
      userId: form.userId ? Number(form.userId) : null,
      status: form.status,
      statusId:
        form.statusId === '' || form.statusId === null
          ? null
          : Number(form.statusId),
      paymentId:
        form.paymentId === '' || form.paymentId === null
          ? null
          : Number(form.paymentId)
    };
  } else {
    const itemsWithName = form.items.map(item => {
      const prod = products.find(p => String(p.id) === String(item.productId));

      return {
        ...item,
        name: prod ? prod.name : (item.name || ''),
        price: parseFloat(item.price) || 0,
        quantity: item.quantity || 1
      };
    });

    const total = itemsWithName.reduce(
      (sum, i) => sum + (parseFloat(i.price) || 0),
      0
    );

    payload = {
      userId: form.userId ? Number(form.userId) : null,
      status: form.status,
      statusId:
        form.statusId === '' || form.statusId === null
          ? null
          : Number(form.statusId),
      items: itemsWithName,
      total,
      paymentId:
        form.paymentId === '' || form.paymentId === null
          ? null
          : Number(form.paymentId)
    };
  }

  try {
    const url =
      mode === 'edit'
        ? `${API}/orders/${form.id}`
        : `${API}/orders`;

    const method = mode === 'edit' ? 'PUT' : 'POST';

    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(payload)
    });

    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Feil ved lagring');

    setSuccess('Ordre lagret!');
    setForm(null);
    setMode('view');
    setSelected(null);
    fetchOrders();
  } catch (err) {
    setError(err.message);
  }
}

  // --- SLETT ORDRE ---
  async function handleDeleteOrder() {
    if (!selected) return;
    if (!window.confirm(`Slett ordre #${selected.id}?`)) return;
    try {
      const res = await fetch(`${API}/orders/${selected.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Feil ved sletting');
      setSuccess('Ordre slettet');
      setSelected(null);
      setForm(null);
      setMode('view');
      fetchOrders();
    } catch (err) {
      setError(err.message);
    }
  }

  function getPaymentName(order) {
    if (!order.PaymentOption && !order.Payment) return '';
    return (
      order.PaymentOption?.name ||
      order.Payment?.name ||
      (paymentOptions.find(opt => opt.id === order.paymentId)?.name) ||
      ''
    );
  }

  function getPaymentNameById(id) {
    return paymentOptions.find(p => String(p.id) === String(id))?.name || '–';
  }

  const totalPrice = (form?.items || []).reduce((sum, i) =>
    sum + (parseFloat(i.price) || 0), 0);

  const chargesTotal = charges.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const chargesUnsettled = charges
    .filter(c => !c.isSettled)
    .reduce((s, c) => s + parseFloat(c.amount || 0), 0);

  function addChargeRow() {
    setChargeRows(prev => [...prev, { description: '', amount: '', paymentId: '' }]);
  }

  function removeChargeRow(idx) {
    setChargeRows(prev => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx));
  }

  function updateChargeRow(idx, field, value) {
    setChargeRows(prev =>
      prev.map((r, i) => i === idx ? { ...r, [field]: value } : r)
    );
  }

  return (
    <div className="flex gap-6 p-4 min-h-[500px]">
      {/* VENSTRE: Ordreliste */}
      <div className={`w-1/2 border-r pr-4 ${mode === 'new' || mode === 'edit' ? 'opacity-40 pointer-events-none select-none' : ''}`}>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-semibold">Ordrer</h2>
          {mode === 'view' && (
            <button
              onClick={handleNew}
              className="bg-[#000099] text-white px-4 py-1 rounded hover:bg-blue-900 transition"
            >
              + Ny
            </button>
          )}
        </div>

        <input
          type="text"
          placeholder="Søk på id, navn, e-post, orgnr eller produkt..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full mb-3 px-3 py-2 border border-gray-300 rounded focus:ring focus:ring-[#000099] text-sm"
        />

        {error && <p className="text-red-500">{error}</p>}

        <ul className="space-y-2 max-h-[650px] overflow-auto">
          {filteredOrders.map(order => {
            const user = order.User;
            const rentalWindow = getRentalWindow(order);
            const statusName = getOrderStatusName(order, statusOptions);
            const colors = getOrderColorClasses(order, statusOptions);

            return (
              <li
                key={order.id}
                onClick={() => {
                  setSelected(order);
                  setForm(null);
                  setMode('view');
                  setError('');
                  setSuccess('');
                }}
                className={`cursor-pointer p-3 rounded border transition ${
                  selected?.id === order.id
                    ? "border-[#000099] ring-1 ring-[#000099]"
                    : colors.outer
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">
                      #{order.id} – {user?.name || 'Ukjent bruker'}
                    </div>

                    <div className="text-sm text-gray-700 mt-1">
                      <span className="font-medium">Leieperiode:</span>{' '}
                      {rentalWindow.start
                        ? `${formatDateTime(rentalWindow.start)} – ${formatDateTime(rentalWindow.end)}`
                        : 'Ingen leieperiode'}
                    </div>

                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Betaling:</span>{' '}
                      {getPaymentName(order) || '–'}
                    </div>

                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Produkt:</span>{' '}
                      {getProductSummary(order)}
                    </div>
                  </div>

                  <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${colors.badge}`}>
                    {statusName || 'Ukjent status'}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* HØYRE: Detaljer, skjema for endre/ny */}
      <div className="w-1/2">
        {(mode === 'edit' || mode === 'new') && form && (
          <form
            className="space-y-5 bg-gray-50 border p-6 rounded-xl shadow-lg"
            onSubmit={handleFormSubmit}
            autoComplete="off"
          >
            <h2 className="text-xl font-semibold mb-2">
              {mode === 'edit' ? "Rediger ordre" : "Ny ordre"}
            </h2>

            <div>
              <label className="block font-medium mb-1">Bruker</label>
              <select
                className="w-full border px-3 py-2 rounded"
                name="userId"
                value={form.userId ?? ''}
                onChange={handleFormChange}
                required
              >
                <option value="">Velg bruker...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email || '(ukjent)'}
                    {u.isBusiness ? ` (Bedrift: ${u.orgNumber})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-medium mb-1">Status</label>
              <select
                className="w-full border px-3 py-2 rounded"
                name="statusId"
                value={form.statusId ?? ''}
                onChange={e => setForm(prev => ({
                  ...prev,
                  statusId: e.target.value === '' ? null : Number(e.target.value)
                }))}
                required
              >
                <option value="">Velg status...</option>
                {statusOptions.map(status => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-medium mb-1">Ordrelinjer</label>
              <div className="space-y-4">
                {form.items.map((item, idx) => (
                  <div key={idx} className="bg-white rounded p-3 border">
                    <div>
                      <select
                        className="border rounded px-2 py-1 w-full"
                        value={item.productId}
                        onChange={e => handleOrderLineChange(idx, 'productId', e.target.value)}
                        required
                      >
                        <option value="">Produkt...</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name} ({p.price_day} kr/døgn)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mt-2 flex flex-col sm:flex-row gap-2">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 mb-0.5 block">Henting</label>
                        <input
                          type="datetime-local"
                          className="border rounded px-2 py-1 w-full"
                          value={item.date_from || ''}
                          onChange={e => handleOrderLineChange(idx, 'date_from', e.target.value)}
                          required
                        />
                      </div>

                      <div className="flex-1">
                        <label className="text-xs text-gray-500 mb-0.5 block">Levering</label>
                        <input
                          type="datetime-local"
                          className="border rounded px-2 py-1 w-full"
                          value={item.date_to || ''}
                          onChange={e => handleOrderLineChange(idx, 'date_to', e.target.value)}
                          required
                        />
                      </div>

                      <div className="flex items-end">
                        <button
                          type="button"
                          className="ml-2 text-red-500 px-2 py-0.5 rounded hover:bg-red-50"
                          disabled={form.items.length === 1}
                          onClick={() => removeOrderLine(idx)}
                          title="Fjern linje"
                        >
                          ×
                        </button>
                      </div>
                    </div>

                    <div className="mt-2">
                      {products.find(p => String(p.id) === String(item.productId))?.allows_rental_quantity && (
                      <div className="mt-2">
                        <label className="text-xs text-gray-500 mb-0.5 block">Antall</label>
                        <input
                          type="number"
                          min="1"
                          max={
                            products.find(p => String(p.id) === String(item.productId))?.rental_stock_qty || 999
                          }
                          className="border rounded px-2 py-1 w-full"
                          value={item.quantity || 1}
                          onChange={e => handleOrderLineChange(idx, 'quantity', e.target.value)}
                        />
                      </div>
                    )}
                      <label className="text-xs text-gray-500 mb-0.5 block">Pris</label>
                      <input
                        type="number"
                        className="border rounded px-2 py-1 w-full"
                        value={item.price}
                        min="0"
                        step="0.01"
                        onChange={e => handleOrderLineChange(idx, 'price', e.target.value)}
                        required
                        placeholder="Pris"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="mt-2 bg-gray-200 text-[#000099] px-3 py-1 rounded hover:bg-gray-300 transition"
                onClick={addOrderLine}
              >
                + Legg til ny
              </button>
            </div>

            <div className="text-right font-bold text-lg text-[#000099]">
              Total: {totalPrice.toLocaleString('no-NO')} kr
            </div>

            <div>
              <label className="block font-medium mb-1">Betalingsalternativ</label>
              <select
                className="w-full border px-3 py-2 rounded"
                name="paymentId"
                value={form.paymentId ?? ''}
                onChange={handleFormChange}
                required
              >
                <option value="">Velg betalingsmåte...</option>
                {paymentOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-4 pt-2">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition"
              >
                Lagre
              </button>
              <button
                type="button"
                onClick={() => { setForm(null); setMode('view'); }}
                className="bg-gray-300 px-6 py-2 rounded hover:bg-gray-400 transition"
              >
                Avbryt
              </button>
            </div>

            {success && <div className="text-green-600">{success}</div>}
            {error && <div className="text-red-600">{error}</div>}
          </form>
        )}

        {mode === 'view' && selected && (
          <div className="space-y-2">
            <h2 className="text-xl font-semibold mb-2">Detaljer</h2>
            <p><strong>Ordre ID:</strong> {selected.id}</p>
            <p>
              <strong>Status:</strong>{' '}
              <span className="capitalize">
                {statusOptions.find(s => s.id === selected.StatusId)?.name || selected.status}
              </span>
            </p>
            <p>
              <strong>Opprettet dato:</strong>{' '}
              {selected.createdAt ? new Date(selected.createdAt).toLocaleString("no-NO") : ''}
            </p>
            <p>
              <strong>Bruker:</strong>{' '}
              {selected.User ? (
                <>
                  {selected.User.name} &nbsp;
                  <span className="text-xs text-gray-500">({selected.User.email})</span><br />
                  {selected.User.isBusiness && (
                    <span className="inline-block bg-yellow-100 text-yellow-900 px-2 py-0.5 rounded text-xs">
                      Bedrift {selected.User.orgNumber ? `(Org: ${selected.User.orgNumber})` : ''}
                    </span>
                  )}
                </>
              ) : 'Ukjent bruker'}
            </p>

            <div>
              <strong>Ordrelinjer:</strong>
              <ul className="pl-4 list-disc">
                {selected.OrderItems?.map(item => (
                  <li key={item.id}>
                    <span className="font-medium">{item.name}</span>
                    {item.date_from && (
                      <> ({formatDateTime(item.date_from)} – {formatDateTime(item.date_to)})</>
                    )}
                    , {item.price} kr
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <strong>Betalingsmåte:</strong>{' '}
              <span className="ml-2">{getPaymentName(selected) || '–'}</span>
            </div>

            {selected.vipps_reference && (
              <div>
                <strong>Vipps-referanse:</strong>{' '}
                <span className="ml-2 break-all">{selected.vipps_reference}</span>
              </div>
            )}

            <div className="text-right font-bold text-lg text-[#000099] mt-2">
              Total: {selected.total?.toLocaleString('no-NO') || '0'} kr
            </div>

            <div className="flex gap-4 pt-4">
              <button
                onClick={handleEdit}
                className="bg-yellow-500 text-white px-6 py-2 rounded hover:bg-yellow-600 transition"
              >
                Rediger
              </button>
              <button
                onClick={handleDeleteOrder}
                className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition"
              >
                Slett
              </button>
              <button
                onClick={() => { setShowChargeForm(s => !s); setChargesError(''); setChargesSuccess(''); }}
                className="bg-[#000099] text-white px-6 py-2 rounded hover:bg-blue-900 transition"
              >
                {showChargeForm ? 'Avbryt' : 'Legg til gebyr'}
              </button>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-semibold">Etterfakturering (gebyr)</h3>
              {chargesError && <div className="text-red-600">{chargesError}</div>}
              {chargesSuccess && <div className="text-green-600">{chargesSuccess}</div>}

              {charges.length === 0 ? (
                <div className="text-gray-500 mt-1">Ingen gebyrer registrert.</div>
              ) : (
                <div className="mt-2 border rounded-md overflow-hidden">
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left px-3 py-2">Dato</th>
                        <th className="text-left px-3 py-2">Beskrivelse</th>
                        <th className="text-right px-3 py-2">Beløp</th>
                        <th className="text-left px-3 py-2">Betaling</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-right px-3 py-2">Handling</th>
                      </tr>
                    </thead>
                    <tbody>
                      {charges.map(c => (
                        <tr key={c.id} className="border-t">
                          <td className="px-3 py-2">{new Date(c.createdAt).toLocaleString('no-NO')}</td>
                          <td className="px-3 py-2">{c.description}</td>
                          <td className="px-3 py-2 text-right">{parseFloat(c.amount || 0).toLocaleString('no-NO')} kr</td>
                          <td className="px-3 py-2">{c.PaymentOption?.name || getPaymentNameById(c.paymentId) || '–'}</td>
                          <td className="px-3 py-2">
                            {c.isSettled ? (
                              <span className="text-green-700 font-medium">Oppgjort</span>
                            ) : (
                              <span className="text-red-600 font-medium">Uoppgjort</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right space-x-2">
                            <button
                              onClick={() => toggleChargeSettled(c.id, c.isSettled)}
                              className="text-sm bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                              title="Bytt oppgjort-status"
                            >
                              {c.isSettled ? 'Sett uoppgjort' : 'Marker oppgjort'}
                            </button>
                            <button
                              onClick={() => deleteCharge(c.id)}
                              className="text-sm bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded"
                            >
                              Slett
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-3 py-2" colSpan={2}>Sum</td>
                        <td className="px-3 py-2 text-right">{chargesTotal.toLocaleString('no-NO')} kr</td>
                        <td className="px-3 py-2" colSpan={2}>Uoppgjort: {chargesUnsettled.toLocaleString('no-NO')} kr</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {showChargeForm && (
              <div className="mt-4 bg-gray-50 border p-4 rounded-md">
                <h4 className="font-semibold mb-2">Legg til gebyr</h4>
                <div className="space-y-3">
                  {chargeRows.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end">
                      <div className="sm:col-span-3">
                        <label className="block text-sm text-gray-600">Beskrivelse</label>
                        <input
                          type="text"
                          className="w-full border px-2 py-1 rounded"
                          placeholder="f.eks. Bompenger"
                          value={row.description}
                          onChange={e => updateChargeRow(idx, 'description', e.target.value)}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm text-gray-600">Beløp (kr)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-full border px-2 py-1 rounded"
                          value={row.amount}
                          onChange={e => updateChargeRow(idx, 'amount', e.target.value)}
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <label className="block text-sm text-gray-600">Betaling</label>
                        <select
                          className="w-full border px-2 py-1 rounded"
                          value={row.paymentId}
                          onChange={e => updateChargeRow(idx, 'paymentId', e.target.value)}
                        >
                          <option value="">–</option>
                          {paymentOptions.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-6 flex justify-end">
                        <button
                          type="button"
                          className="text-red-600 text-sm px-2 py-1 rounded hover:bg-red-50"
                          onClick={() => removeChargeRow(idx)}
                          disabled={chargeRows.length === 1}
                          title="Fjern rad"
                        >
                          Fjern rad
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-between">
                    <button
                      type="button"
                      className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded"
                      onClick={addChargeRow}
                    >
                      + Ny rad
                    </button>
                    <div className="space-x-2">
                      <button
                        type="button"
                        className="bg-gray-300 hover:bg-gray-400 px-4 py-1 rounded"
                        onClick={() => { setShowChargeForm(false); setChargesError(''); setChargesSuccess(''); }}
                      >
                        Avbryt
                      </button>
                      <button
                        type="button"
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-1 rounded"
                        onClick={saveCharges}
                      >
                        Lagre gebyrer
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {success && <div className="text-green-600">{success}</div>}
            {error && <div className="text-red-600">{error}</div>}
          </div>
        )}

        {mode === 'view' && !selected && (
          <p className="text-gray-600">Velg en ordre for detaljer.</p>
        )}
      </div>
    </div>
  );
}